<!-- Main Content -->
<div fxLayout="column" style="width: 100%; height: 100%">
	<!-- Unified Top Bar -->
	<div class="unified-toolbar">
		<div class="toolbar-section toolbar-left">
			<button mat-icon-button [routerLink]="['/formly/list']" matTooltip="Back to List">
				<mat-icon>arrow_back</mat-icon>
			</button>
			<div class="script-title" *ngIf="scriptInfo">
				<span class="script-id">{{ scriptInfo.scriptId }}</span>
				<span class="separator">-</span>
				<span class="script-name">{{ scriptInfo.name }}</span>
			</div>
		</div>

		<div class="toolbar-section toolbar-center">
			<!-- Quick Add (JSON Editor tab only) -->
			<mat-form-field
				*ngIf="selectedTabIndex === 2"
				class="quick-add-field"
				appearance="outline"
				subscriptSizing="dynamic"
			>
				<mat-icon matPrefix>search</mat-icon>
				<input
					#quickAddInput
					type="text"
					matInput
					placeholder="Quick Add"
					[formControl]="quickAddControl"
					[matAutocomplete]="quickAddAuto"
					(input)="filterQuickAdd()"
					(focus)="filterQuickAdd()"
				/>
				<mat-autocomplete
					autoActiveFirstOption
					#quickAddAuto="matAutocomplete"
					[panelWidth]="'250px'"
					(optionSelected)="onQuickAddSelected($event)"
					requireSelection
				>
					@for (option of filteredQuickAddOptions | async; track option) {
						<mat-option [value]="option">{{ option.name }}</mat-option>
					}
				</mat-autocomplete>
			</mat-form-field>

			<!-- View Tabs -->
			<nav mat-tab-nav-bar class="toolbar-tabs" [tabPanel]="tabPanel">
				<a
					mat-tab-link
					*ngFor="let tab of tabs; let i = index"
					(click)="selectedTabIndex = i; onTabChange(i)"
					[active]="selectedTabIndex === i"
				>
					<mat-icon>{{ tab.icon }}</mat-icon>
					<span>{{ tab.label }}</span>
				</a>
			</nav>
		</div>

		<div class="toolbar-section toolbar-right">
			<button mat-icon-button (click)="undo()" [disabled]="!canUndo" matTooltip="Undo (Ctrl+Z)">
				<mat-icon>undo</mat-icon>
			</button>
			<button mat-icon-button (click)="redo()" [disabled]="!canRedo" matTooltip="Redo (Ctrl+Y)">
				<mat-icon>redo</mat-icon>
			</button>
			<button mat-button (click)="onManageTranslations()" matTooltip="Manage Translations">
				<mat-icon>translate</mat-icon>
				Translate
			</button>
			<button
				mat-raised-button
				color="primary"
				(click)="save()"
				[disabled]="!hasChanges || !isJsonValid"
				matTooltip="Save changes to the script"
			>
				<mat-icon>save</mat-icon>
				Save
			</button>
			<button mat-icon-button [matMenuTriggerFor]="moreMenu" matTooltip="More Actions">
				<mat-icon>more_vert</mat-icon>
			</button>
			<mat-menu #moreMenu="matMenu">
				<button mat-menu-item (click)="editScriptInfo()">
					<mat-icon>edit</mat-icon>
					<span>Edit Script Info</span>
				</button>
				<button mat-menu-item (click)="importFromTripetto()">
					<mat-icon>file_upload</mat-icon>
					<span>Import from Tripetto</span>
				</button>
				<a mat-menu-item href="https://formly.dev" target="_blank" rel="noopener noreferrer">
					<mat-icon>help_outline</mat-icon>
					<span>Formly Documentation</span>
				</a>
			</mat-menu>
		</div>
	</div>

	<!-- Content Area -->
	<div class="builder-container">
		<mat-tab-nav-panel #tabPanel class="tab-panel">
			<!-- Visual Builder Tab Content -->
			<div class="tab-content" *ngIf="selectedTabIndex === 0">
				<mat-spinner *ngIf="!jsonFieldsReady"></mat-spinner>
				<nx-ahs-visual-builder
					*ngIf="jsonFieldsReady"
					[(fields)]="fields"
					[scriptId]="scriptId"
					(onFieldsChanged)="onVisualBuilderFieldsChanged()"
				>
				</nx-ahs-visual-builder>
			</div>

			<!-- Flow Chart Tab Content -->
			<div class="tab-content flow-chart-tab" *ngIf="selectedTabIndex === 1">
				<nx-ahs-flow-chart [fields]="fields"></nx-ahs-flow-chart>
			</div>

			<!-- JSON Editor Tab Content -->
			<div class="tab-content" *ngIf="selectedTabIndex === 2">
				<div fxLayout="row" fxLayoutGap="16px" style="width: 100%; height: 100%">
					<div fxLayout="column" fxFlex fxLayoutGap="16px">
						<div id="jsoneditorBuilder" fxFlex>
							<mat-spinner *ngIf="!jsonFieldsReady"></mat-spinner>
							<nx-ahs-json-builder
								#fieldEditor
								fxFlex
								[content]="content"
								[hasChanges]="hasChanges"
								[isJsonValid]="isJsonValid"
								(jsonChanged)="jsonChanged($event)"
								(jsonValidityChanged)="onJsonValidityChanged($any($event))"
								(saveClicked)="save()"
								(manageTranslationsClicked)="onManageTranslations()"
								*ngIf="jsonFieldsReady"
							></nx-ahs-json-builder>
						</div>
					</div>
					<!-- Model Content -->
					<div fxLayout="column" fxFlex="25%" fxLayoutGap="16px">
						<div fxFlex>
							<nx-ahs-vanilla-json-editor
								#modelEditor
								fxFlex
								[content]="modelContent"
								(jsonChanged)="modelJsonChanged($event)"
								[subtractHeight]="185"
							></nx-ahs-vanilla-json-editor>
						</div>
					</div>
					<!-- Form UI Content -->
					<div fxLayout="column" fxFlex="25%" fxFlex.sm="40%" fxFlex.xs="50%">
						<div fxLayout="row" class="custom-toolbar pb-8" fxLayoutGap="5px">
							<button mat-raised-button class="toolbar-button" (click)="formlyCollector.resetForm(true)">
								Reset
							</button>
							<button mat-raised-button class="toolbar-button" (click)="formlyCollector.reloadForm()">
								Reload
							</button>
							<button
								mat-stroked-button
								class="toolbar-button"
								(click)="preview()"
								[color]="previewEnabled ? 'warn' : 'primary'"
							>
								Preview
							</button>
						</div>
						<div style="overflow: auto">
							<mat-card>
								<mat-card-content class="builder-preview-content">
									<lib-formly-collector
										#formlyCollector
										[fields]="fields"
										[model]="model"
										[options]="options"
										[scriptId]="scriptId"
										[isFromBuilder]="true"
										(modelChanged)="modelChanged($event)"
										(allReady)="formlyFormReady($event)"
									></lib-formly-collector>
								</mat-card-content>
							</mat-card>
						</div>
					</div>
				</div>
			</div>
		</mat-tab-nav-panel>
	</div>
	<!-- Close builder-container -->
</div>
<!-- Close main column -->
